import React, { Component } from 'react';
import { AuthConsumer } from 'react-check-auth';
import { Redirect } from 'react-router-dom';
import Login from './Login';
import BasicForm from './BasicForm';
import Krb5Form from './Krb5Form';
import Success from './Success';
import Failure from './Failure';
import Message from './Message';
import Logout from './Logout';
import Status from './Status';

/* States and Transitions
   Definition of State: a stable async period of waiting
   for an event (i.e. UI input or message). Upon receipt
   of an event it can Transition (to another State).
   Named Transitions are defined here; some Anonymous
   events (indicated) also cause transition.
 */

const sm = {
  login: {
    BASIC_START: 'basic_form',
    KRB5_START: 'krb5_form',
    CAC_START: 'cac_loading',
    CANCEL: 'cancel_login'
  },
  cancel_login: {
    /* BSF I'm adding this state because CANCEL from login needs a
       destination. This ensures that the cancel_log attempt steps (in
       the Command and Render blocks) are given a chance to execute.
       And, WTH, if they fail, they go back to login state -- no harm,
       no foul.  NB this is NOT an exit state, so it needs a
       definition. (Unlike the unnamed success state which _does_ exit
       without touching much (anything?) on its way out.
     */
    FAILURE: 'login'
  },
  basic_form: {
    BASIC_LOGIN: 'basic_loading',
    BACK_TO_OPTIONS: 'login'
  },
  basic_loading: {
    /* Anon exit transition of login success, caused by handleResult */
    FAILURE: 'error'
  },
  krb5_form: {
    KRB5_LOGIN: 'krb5_loading',
    BACK_TO_OPTIONS: 'login'
  },
  krb5_loading: {
    /* Anon exit transition of krb5 success, caused by handleResult */
    GOT_MESSAGE: 'krb5_messaging',
    FAILURE: 'error'
  },
  krb5_messaging: {
    MESSAGE_ACKED: 'login_result'
  },
  login_result: {
    FAILURE: 'error'
  },
  cac_loading: {
    /* Anon exit transition of CAC success, caused by handleResult */
    FAILURE: 'error'
  },
  error: {
    TRY_AGAIN: 'login'
  },
  logout: {
    DO_LOGOUT: 'logging_out',
  },
  logging_out: {
    FAILURE: 'error'
  },
  dashboard: {
    /* Do-nothing.  Merely serves as a destination for the "Home" button. */
  }
};

class StateMachine extends Component {
  constructor(props) {
    super(props);

    this.state = {
      step: props.step
    };
  }

  /* State Transition mechanisms */

  doTransition(action) {
    const curStep = this.state.step;
    const nextStep = sm[curStep][action.type];

    console.log("doTransition @ %s: %s -> %s", curStep, action.type, nextStep);

    if (nextStep) {
      /* Process the transition */
      this.command(nextStep, action);

      /* Render the new State */
      this.setState({
        step: nextStep
      });
    }
  }

  transition(type) {
    return () => this.doTransition({ type: type });
  }

  /* BSF please explain the WithData part */
  transitionWithData(type) {
    return data => this.doTransition({ type: type, data: data });
  }

  /* Async operations that drive transitions when settled.
     These are modeled as JS Promises 
     and have callbacks for success, some also for failure.
   */

  attemptCancelLogin() {
    return fetch("/cancel", {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
      },
      credentials: 'include'
    });
  }

  attemptCACLogin() {
    return fetch("/cac", {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
      },
      credentials: 'include'
    });
  }

  attemptBasicLogin(data) {
    return fetch("/basic", {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(data)
    });
  }

  attemptKRB5Login(data) {
    return fetch("/krb5", {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(data)
    });
  }

  attemptLogout(data) {
    return fetch("/logout", {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(data),
      credentials: 'include'
    });
  }

  /* State Transition handlers
     All are modeled as Promise callbacks.
   */

  validateResponse = (response) => {
    if (response.headers.get('Content-Type') !== "application/json") {
      return Promise.reject("There is an error with the server.");
    } else {
      /* Promise.then() returns this as a promise */
      return response.json();
    }
  }

  handleMessage = (response) => {
    let message_str;
    let redirect_str;

    message_str = response['message'];
    if (message_str == null) {
      console.log("handleMessage no response message found");
      return response; /* auto wrapped as Promise by .then() */
    } else {
      console.log("handleMessage found a response message: ", message_str);

      this.setState({
        message: message_str
      });

      redirect_str = response;
      console.log("handleMessage found this redirect: ", redirect_str['redirect_url']);
      this.setState({
        redirect: redirect_str
      });
      
      return Promise.reject(response);
    }
  }

  saveResult = (result) => {
    console.log("saveResult storing a result");
      this.setState({
        result: result
      });

    this.doTransition({ type: 'GOT_MESSAGE', data: result });

    return Promise.resolve("Saved"); 
  }

  recallResult = () => {
    let redirect_str;

    redirect_str = this.state.redirect;
    console.log("recallResult redirecting to: ", redirect_str['redirect_url']);
    window.location.replace(redirect_str['redirect_url']);
  }

  handleResult = (result) => { 
    console.log("handleResult with: %s", result);
    if ('error' in result) {
      console.log("handleResult returning error");
      return Promise.reject(result);
    }

    /* This serves as an Anon SUCCESS transition to Exit State
       for the auth operations:
       basic_loading, krb5_loading, cac_loading.
    */
    console.log("handleResult redirecting to: ", result['redirect_url']);
    window.location.replace(result['redirect_url']);
  }

  handleReject = (result) => {
    let error = result['error'];
    let explainer = result['explanation'];
    let errorText;

    if (error instanceof Error) {
      errorText = error.message;
    } else {
      errorText = error;
    }
    console.log("handleReject sending FAILURE with: ", errorText);
    this.doTransition({ type: 'FAILURE', data: { error: errorText, explanation: explainer } });
  }

  /* State Transition commands */

  command(nextStep, action) {
    switch (nextStep) {

      case 'cancel_login':
        this.attemptCancelLogin()
        .then(this.validateResponse)
        .then(this.handleResult)
        .catch(this.handleReject);
        break;

      case 'basic_loading':
        this.attemptBasicLogin(action.data)
        .then(this.validateResponse)
        .then(this.handleResult)
        .catch(this.handleReject);
        break;

      case 'krb5_loading':
        this.attemptKRB5Login(action.data)
        .then(this.validateResponse)
        .then(this.handleMessage)
        .then(this.handleResult,this.saveResult)
        .catch(this.handleReject);
        break;

      case 'login_result':
        Promise(this.recallResult(action.data))
        .then(this.handleResult)
        .catch(this.handleReject);
        break;

      case 'cac_loading':
        this.attemptCACLogin()
        .then(this.validateResponse)
        .then(this.handleResult)
        .catch(this.handleReject);
        break;

      case 'error':
        this.setState({
          error: action.data['error']
        });
        this.setState({
          explanation: action.data['explanation']
        });
        break;

      case 'logging_out':
        this.attemptLogout(action.data)
        .then(this.validateResponse)
        .then(this.handleResult)
        .catch(this.handleReject);
        break;

      default:
        /* BSF: how about sending it back to a known state -- maybe error? */
        break;
    }
  }

  render() {
    switch (this.state.step) {
      case 'login':
        return (
          <Login
            onBasicLogin={this.transition('BASIC_START')}
            onCACLogin={this.transition('CAC_START')}
            onKRB5Login={this.transition('KRB5_START')}
            onCancel={this.transition('CANCEL')}
          />
        );
      case 'cancel_login':
        return (<Status level="info" heading="Canceling Login" body="Please Wait..." />);
      case 'basic_form':
        return (
          <BasicForm
            onLogin={this.transitionWithData('BASIC_LOGIN')}
            onBackToOptions={this.transition('BACK_TO_OPTIONS')}
          />
        );
      case 'basic_loading':
        return (<Status level="info" heading="Attempting Login" body="Please Wait..." />);
      case 'krb5_form':
        return (
          <Krb5Form
            onLogin={this.transitionWithData('KRB5_LOGIN')}
            onBackToOptions={this.transition('BACK_TO_OPTIONS')}
          />
        );
      case 'krb5_loading':
        return (<Status level="info" heading="Attempting Login" body="Please Wait..." />);
      case 'krb5_messaging':
        return (
          <Message
            onMessageAck={this.transitionWithData('MESSAGE_ACKED')}
            message={this.state.message}
          />
        );
      case 'login_result':
        return (<Status level="info" heading="Continuing Login" body="Please Wait..." />);
      case 'cac_loading':
        return (<Status level="info" heading="Attempting Login" body="Please Wait..." />);
      case 'success':
        return (<Success />);
      case 'error':
        return (
          <Failure
            onTryAgain={this.transition('TRY_AGAIN')}
            error={this.state.error}
            explanation={this.state.explanation}
          />
        );
      case 'logout':
        let logoutTransition = sessions => this.transitionWithData('DO_LOGOUT')({'logout': sessions});
        return (
          <AuthConsumer>
            {({userInfo, isLoading, error}) => {
              /* I don't like this API design from react-check-auth, but
               * isLoading is false before the request as been made.  As a
               * result, check to see if userInfo is false and error is null to
               * verify the request hasn't even been sent yet. */
              if (isLoading || (!userInfo && !error)) {
                return (<Status level="info" heading="Attempting Logout" body="Please Wait..." />);
              } else {
                if (userInfo) {
                  return (
                    <Logout
                      onRPOnly={() => logoutTransition(['rp'])}
                      onRPAndSession={() => logoutTransition(['rp', 'session'])}
                    />
                  );
                } else {
                  logoutTransition(['rp']);
                }
              }
            } }
          </AuthConsumer>
        );
      case 'logging_out':
        return (<Status level="info" heading="Attempting Logout" body="Please Wait..." />);
      case 'landing':
        return (<Redirect to="/" />);
      case 'dashboard':
        return (<Status level="success" heading="Logged In" body="You have successfully authenticated." />);
      default:
        return (<Status level="error" heading="Error" body="An error occurred with the website frontend." />);
    }
  }
}

export default StateMachine;



// WEBPACK FOOTER //
// ./src/StateMachine.jsx